import { useMemo } from 'react';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { NotificationInboxRow } from './NotificationInboxRow';
import { ReleaseGroupInboxRow } from './ReleaseGroupInboxRow';
import {
  useDismissNotification,
  useDismissNotifications,
  useNotifications,
} from '../useNotifications.js';
import { groupReleaseNotifications } from '../groupNotifications.js';
import type { Notification } from '../../shared/types.js';

export interface NotificationsTopSectionProps {
  /**
   * Id of the currently selected notification in the inbox right pane, or
   * `null` when another item type (agent / flow run) is selected. Drives
   * the per-row highlight.
   */
  selectedNotificationId?: string | null;
  /**
   * Called when the user clicks a notification row. The host inbox view
   * is responsible for swapping its right pane to a notification detail
   * panel — this component doesn't navigate on its own.
   */
  onSelect?: (notification: Notification) => void;
}

/**
 * Pinned section rendered above the standard inbox buckets. Lists every
 * non-dismissed notification visible to the current user. Hidden entirely
 * when there's nothing to show so the inbox stays scannable.
 *
 * Notifications participate in the inbox's master/detail selection just
 * like agents and flow runs — `onSelect` is wired from `InboxView` so a
 * click swaps the right pane to `NotificationDetailPanel`.
 */
export function NotificationsTopSection({
  selectedNotificationId = null,
  onSelect,
}: NotificationsTopSectionProps = {}) {
  const { notifications } = useNotifications();
  const dismiss = useDismissNotification();
  const dismissMany = useDismissNotifications();
  const entries = useMemo(() => groupReleaseNotifications(notifications), [notifications]);

  if (notifications.length === 0) return null;

  return (
    // `w-full min-w-0` anchors this flex column to the parent's width
    // (the Radix `ScrollArea` viewport wraps children in a
    // `display: table; min-width: 100%;` div, so a flex child without
    // an explicit `w-full` is free to grow past the viewport when its
    // descendants have long titles). `min-w-0` lets the flex column
    // shrink below its content's intrinsic width so the `truncate`
    // declarations on each row's title/subtitle actually clip.
    <div
      className="flex w-full min-w-0 flex-col gap-2 px-3 pt-3 pb-2"
      data-testid="notifications-top-section"
    >
      <SectionLabel>Notifications</SectionLabel>
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {entries.map((entry) =>
          entry.kind === 'release-group' ? (
            <ReleaseGroupInboxRow
              key={`release-group:${entry.latest.id}`}
              latest={entry.latest}
              others={entry.others}
              selected={entry.ids.some((id) => selectedNotificationId === id)}
              onSelect={() => onSelect?.(entry.latest)}
              onDismissAll={() => dismissMany.mutate(entry.ids)}
            />
          ) : (
            <NotificationInboxRow
              key={entry.notification.id}
              notification={entry.notification}
              selected={selectedNotificationId === entry.notification.id}
              onSelect={() => onSelect?.(entry.notification)}
              onDismiss={(id) => dismiss.mutate(id)}
            />
          ),
        )}
      </div>
    </div>
  );
}
